import React, { useCallback } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { IRevaultInformation } from 'features/payment/types';
import { shouldReVault } from 'features/payment/utils';
import { useIntl } from 'react-intl';

import { PaymentMethodAddNewItemOption } from 'components/payment-method-add-new-item-option';
import PaymentMethodOption from 'components/payment-method-option';
import { usePaymentCheckoutRedesignExperiment } from 'experiments/use-payment-checkout-redesign-experiment';
import { logAddPaymentMethodClick } from 'state/amplitude';
import { emptyCartContext, useCartContext } from 'state/cart';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import noop from 'utils/noop';
import { routes } from 'utils/routing';

import { ADD_PREPAID_CARD } from '../constants';
import { StyledAddNewCreditCardIcon } from '../styled';

import { PaymentMethodOptionItemDivider, StyledOption } from './styled';
import { PaymentMethodOptionProps } from './types';

export const PaymentMethodOptions: React.FC<PaymentMethodOptionProps> = ({
  collapsed,
  setCollapsed,
  unSelectedMethods,
  handleSelect,
  setIsAddNewCardOptionSelected,
  setIsAddNewGiftCardOptionSelected,
  showAddNewGiftCardOption,
  showAddNewCardOption,
  isQuickPay = false,
  setIsQuickPayMethodSelected = noop,
}) => {
  const { formatMessage } = useIntl();
  const router = useRouter();
  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);
  const isPaymentRedesignEnabled = usePaymentCheckoutRedesignExperiment();
  const { isOrbital: isOrbitalPaymentEnabled, setAccountToDelete } = usePaymentContext();

  const handleUnselectedMethodPress = useCallback(
    (newfdAccountId: any) => {
      setIsAddNewCardOptionSelected(false);
      setIsAddNewGiftCardOptionSelected(false);
      handleSelect(newfdAccountId);
      setCollapsed(true);
    },
    [handleSelect, setCollapsed, setIsAddNewGiftCardOptionSelected, setIsAddNewCardOptionSelected]
  );

  const handleAddNewCardOptionPress = useCallback(() => {
    logAddPaymentMethodClick();
    setIsAddNewCardOptionSelected(true);
    setIsAddNewGiftCardOptionSelected(false);
    handleSelect();
    setCollapsed(true);
  }, [
    handleSelect,
    setCollapsed,
    setIsAddNewGiftCardOptionSelected,
    setIsAddNewCardOptionSelected,
  ]);

  const handleAddNewGiftCardOptionPress = useCallback(() => {
    if (!isQuickPay) {
      setIsAddNewGiftCardOptionSelected(true);
      setIsAddNewCardOptionSelected(false);
      handleSelect();
      setCollapsed(true);
    }

    router.navigate(routes.addGiftCard);
  }, [
    isQuickPay,
    router,
    setIsAddNewGiftCardOptionSelected,
    setIsAddNewCardOptionSelected,
    handleSelect,
    setCollapsed,
  ]);

  const isInCart = useCartContext() !== emptyCartContext;

  if (collapsed) {
    return null;
  }

  return (
    <>
      {unSelectedMethods.map(method => {
        const revaultInformation: IRevaultInformation = shouldReVault({
          accountIdentifier: method.accountIdentifier || '',
          isOrbitalPaymentEnabled,
          methods: unSelectedMethods,
        });

        return (
          <StyledOption key={method.accountIdentifier}>
            <PaymentMethodOptionItemDivider />
            <PaymentMethodOption
              method={method}
              onPress={handleUnselectedMethodPress}
              disableMethod={!!method.prepaid && !enableGiftCard}
              hideBorder
              revaultInformation={revaultInformation}
              fromCheckout={true}
              collapsed={collapsed}
              isQuickPay
            />
          </StyledOption>
        );
      })}

      {showAddNewCardOption && (
        <StyledOption>
          <PaymentMethodOptionItemDivider />
          <PaymentMethodAddNewItemOption
            testID="add-new-card"
            text={formatMessage({ id: 'addNewCard' })}
            Icon={<StyledAddNewCreditCardIcon />}
            onPress={() => {
              if (!isQuickPay) {
                handleAddNewCardOptionPress();
              } else {
                setIsQuickPayMethodSelected(true);
                if (isPaymentRedesignEnabled) {
                  setAccountToDelete('');
                } else {
                  router.navigate(`${routes.addCard}?isInCart=${isInCart}`);
                }
              }
            }}
          />
        </StyledOption>
      )}

      {showAddNewGiftCardOption && (
        <StyledOption>
          <PaymentMethodOptionItemDivider />
          <PaymentMethodAddNewItemOption
            testID="add-gift-card"
            text={formatMessage({ id: ADD_PREPAID_CARD })}
            Icon={<Icon variant="crownCreditCard" />}
            onPress={handleAddNewGiftCardOptionPress}
          />
        </StyledOption>
      )}
    </>
  );
};
